import { NavigateService } from '@shared/services/navigate.service';
import { StorageService } from '@core/services/storage.service';
import { TaskType } from '@shared/enums/task-type.enum';
import { catchError, filter, tap } from 'rxjs/operators';
import { EMPTY, Subscription } from 'rxjs';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter
} from '@angular/core';
import { TaskSidenavManager } from '@shared/modules/task-sidenav/managers/task-sidenav.manager';
import { Task } from '@shared/models/task.model';
import { Config } from '@shared/configs/config';
import {
  ListTaskEvent,
  ListTaskEventType,
  ListTaskService
} from '@shared/modules/list/services/list-task.service';
import { TaskController } from '@shared/controllers/task.controller';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { Project } from '@modules/projects/shared/models/project.model';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ChangeType } from '@shared/modules/task-sidenav/enums/change-type.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { merge, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { Validators, UntypedFormControl } from '@angular/forms';
import { InputV2Component } from '@shared/modules/forms-v2/components/input-v2/input-v2.component';
import { TaskChangesSummaryComponent } from '../task-changes-summary/task-changes-summary.component';
import { AttachmentType } from '@shared/components/text-attachments-input/enums/text-attachment-type.enum';
import { AttachmentService } from '@shared/services/attachment.service';
import { getFileType } from '@shared/helpers/basic-file-type.helper';
import { MatDialog } from '@angular/material/dialog';
import { ImageAttachmentModalComponent } from '@shared/components/image-attachment-modal/image-attachment-modal.component';
import { NgZone } from '@angular/core';
import { RICH_MEDIA_QUILL_FORMATS, RICH_MEDIA_QUILL_TOOLBAR } from '@shared/consts/rich-media-quill.const';
import { QuillAttachmentSourcePickerService } from '@shared/modules/attachment-manage/services/quill-attachment-source-picker.service';
import { AuthFilePipe } from '@shared/pipes/authFile.pipe';

type EditorTarget = 'content' | 'internalNote';

@Component({
  selector: 'task-basic-preview-data',
  templateUrl: './task-basic-preview-data.component.html',
  styleUrls: ['./task-basic-preview-data.component.scss']
})
export class TaskBasicPreviewDataComponent implements OnInit, OnDestroy {
  Config = Config;
  TaskType = TaskType;

  sub: Subscription = new Subscription();
  taskManager: TaskSidenavManager = new TaskSidenavManager();
  canProjectAdd = true;
  isProjectRequired = false;
  private readonly refreshGroups$ = new Subject<void>();
  private projectIdCtrl = this.service.form.get(TASK_SIDENAV_FORM.projectId);
  @ViewChild('titleInput') protected titleInput?: InputV2Component;
  @ViewChild('descriptionEditor') protected descriptionEditor?: any;
  @ViewChild('internalNoteEditor') protected internalNoteEditor?: any;
  @ViewChild(TaskChangesSummaryComponent) protected changesSummary?: TaskChangesSummaryComponent;
  protected titleEditControl = new UntypedFormControl('', [Validators.required]);
  protected contentEditControl = new UntypedFormControl(null);
  protected internalNoteEditControl = new UntypedFormControl(null);
  protected editingTitle = false;
  protected editingDescription = false;
  protected editingInternalNote = false;
  protected clipboardFileName = '';
  @Output() attachmentAdded: EventEmitter<any> = new EventEmitter<any>();
  protected readonly quillFormats = RICH_MEDIA_QUILL_FORMATS;
  private descriptionPasteTarget?: HTMLElement;
  private internalNotePasteTarget?: HTMLElement;
  private readonly preserveInlineEditorOnBlur: Record<EditorTarget, boolean> = {
    content: false,
    internalNote: false
  };
  protected descriptionModules!: any;
  protected internalNoteModules!: any;

  get isProcessTaskContext(): boolean {
    return (
      !!this.service?.isProcessTaskContext ||
      this.service?.groupType === 'activity-template' ||
      !!this.service?.activityTemplateContext
    );
  }

  get allowInlineEditing(): boolean {
    return !this.service?.task?.isDeleted;
  }

  get shouldHideCheckbox(): boolean {
    const type = String(this.service?.task?.type || '');
    return type === TaskType.TYPE_MESSAGE || type === TaskType.TYPE_FOLLOW_UP;
  }

  get shouldShowInlineDescriptionPlaceholder(): boolean {
    return !this.editingDescription && !this.service?.safeContent;
  }

  get shouldShowDescriptionValue(): boolean {
    return !this.editingDescription && !!this.service?.safeContent;
  }

  get shouldShowInternalNoteValue(): boolean {
    return !this.editingInternalNote && !!this.service?.safeContentInternalNote;
  }

  get titlePlaceholderKey(): string {
    return this.isProcessTaskContext ? 'Projects.processTaskPlaceholders.title' : 'ProjectContacts.List.name';
  }

  get descriptionPlaceholderKey(): string {
    if (!this.isProcessTaskContext) {
      return `Projects.enterDescription-${this.service.FormType}`;
    }

    const type = this.service?.task?.type || this.service?.activityTemplateTaskType || 'task';
    return `Projects.processTaskPlaceholders.description.${type}`;
  }

  taskGroups$ = (this.listTaskService.isMyWorkTaskLists
    ? this.projectIdCtrl.valueChanges.pipe(
        startWith(this.projectIdCtrl.value),
        distinctUntilChanged(),
        tap((pid) => {
          if (!this.projectIdCtrl.pristine && this.service.groupType !== 'project') {
            this.resetProjectControl();
          }
          this.listTaskService.projectId = pid;
        }),
        switchMap((pid) => {
          const groupType = pid || this.service.groupType === 'project' ? 'project' : 'without_project';
          return this.taskService.getWorkspaceTaskGroupByType(groupType);
        })
      )
    : merge(
        this.refreshGroups$,
        this.projectIdCtrl.valueChanges.pipe(
          startWith(this.projectIdCtrl.value),
          distinctUntilChanged(),
          tap((pid) => (this.service.projectID = pid))
        )
      ).pipe(
        switchMap(() => {
          const formTypeValue = this.service.form.get(this.service.TASK_SIDENAV_FORM.type)!.value;
          const currentProjectId = this.projectIdCtrl.value;

          if (formTypeValue === 'change') return of([]);
          return this.taskService.getTaskGroupByType(formTypeValue, currentProjectId);
        })
      )
  ).pipe(
    tap((groups) => this.syncProjectGroupSelection((groups ?? []) as Array<{ id: number; name: string }>)),
    map((groups) =>
      ((groups ?? []) as []).map(({ id, name }) => ({
        id,
        name: name === 'general' ? this.t.instant('ProjectTasks.TaskGroup.general') : name
      }))
    )
  );

  protected readonly changeTypes = Object.values(ChangeType).map((key) => ({
    id: key as ChangeType,
    name: this.t.instant(`Projects.changeType.${key}`)
  })) as Array<{ id: ChangeType; name: string }>;

  constructor(
    public storage: StorageService,
    public service: TaskSidenavService,
    private changes: ChangeDetectorRef,
    private listTaskService: ListTaskService,
    private navigateService: NavigateService,
    private snackBarService: SnackBarService,
    public t: TranslateService,
    private sanitizer: DomSanitizer,
    private taskService: TaskService,
    private elRef: ElementRef<HTMLElement>,
    private attachmentService: AttachmentService,
    private dialog: MatDialog,
    private zone: NgZone,
    private quillAttachmentSourcePicker: QuillAttachmentSourcePickerService,
    private authFile: AuthFilePipe
  ) {
    const component = this;
    this.descriptionModules = {
      toolbar: {
        container: RICH_MEDIA_QUILL_TOOLBAR,
        handlers: {
          image: function (this: { container: HTMLElement }) {
            const button = this.container.querySelector<HTMLElement>('button.ql-image');
            component.selectQuillAttachment('content', button);
          }
        }
      },
      imageDropAndPaste: {
        handler: this.uploadImage.bind(this, 'content')
      }
    };
    this.internalNoteModules = {
      toolbar: {
        container: RICH_MEDIA_QUILL_TOOLBAR,
        handlers: {
          image: function (this: { container: HTMLElement }) {
            const button = this.container.querySelector<HTMLElement>('button.ql-image');
            component.selectQuillAttachment('internalNote', button);
          }
        }
      },
      imageDropAndPaste: {
        handler: this.uploadImage.bind(this, 'internalNote')
      }
    };
  }

  ngOnInit() {
    this.listenListTaskEvents();
    this.subscribeTaskUpdate();
    this.initPermissions();
    this.subscribeProjectUpdate();
    this.syncInlineEditors();
    requestAnimationFrame(() => this.initEditorCustomMatcher('internalNote'));
  }

  initPermissions() {
    this.canProjectAdd = true;

    if (this.listTaskService.isMyWorkTaskLists) {
      if (this.service.groupType === 'project') {
        this.isProjectRequired = true;
      }
    }
    this.changes.detectChanges();
  }

  listenListTaskEvents() {
    const actions = [ListTaskEventType.UPDATE_TASK];
    const sub = this.listTaskService.emitter
      .pipe(filter((i) => actions.indexOf(i.type) > -1))
      .subscribe(this.updateTask.bind(this));
    this.sub.add(sub);
  }

  updateTaskFromCheckbox({ task, subtasksCompleted }: { task: Task; subtasksCompleted?: boolean }) {
    const ctrl = new TaskController(task);
    ctrl
      .changeTaskStatus(task, this.listTaskService.isMyWorkTaskLists && subtasksCompleted === false)
      .pipe(
        tap(() => {
          this.snackBarService.success(this.getStatusChangedMessage(task));
          this.listTaskService.emitter.emit({
            type: ListTaskEventType.UPDATE_COUNTERS,
            data: { projectId: task?.project?.id, groupType: task.type }
          });

          this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK_LIST });
          this.changes.detectChanges();
        }),
        catchError(() => {
          this.snackBarService.error(this.t.instant('Tasks.errorStatusChange'));
          return EMPTY;
        })
      )
      .subscribe();
  }

  openGmailUrl(event: MouseEvent): void {
    event.stopPropagation();

    const url = this.service?.task?.gmailUrl;
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private getStatusChangedMessage(task: Task): string {
    const taskType =
      task?.type === 'company-task' || task?.type === 'client-task' ? 'task' : task?.type || 'task';
    const actionKey = task?.status === 'completed' ? 'completed' : 'created';

    return this.t.instant('Tasks.successStatusChangeAction', {
      taskType: this.t.instant(`Tasks.TaskType.${taskType}`),
      action: this.t.instant(`Tasks.statusAction.${actionKey}`)
    });
  }

  updateTask(e: ListTaskEvent) {
    if (e.data.id === this.service.task.id) {
      this.service.initPreviewState(e.data);
      this.service.form.get(TASK_SIDENAV_FORM.status).patchValue(e.data.status, { emitEvent: false });
      this.syncInlineEditors();
      this.changes.detectChanges();
    }
  }

  onStatusChange(task: Task) {
    this.service.setStatusCompleted(task);
  }

  checkedSubtasks(checked: boolean) {}

  subscribeProjectUpdate() {
    const sub = this.service.form
      .get(TASK_SIDENAV_FORM.projectId)
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.service.pendingProjectGroupSync = true;
          this.service.form.get(TASK_SIDENAV_FORM.protocolId).setValue(null);
        }),
        debounceTime(0)
      )
      .subscribe(() => this.refreshGroups$.next());
    this.sub.add(sub);
  }

  resetProjectControl(): void {
    if (this.listTaskService.isMyWorkTaskLists) {
      this.service.form.get(this.service.TASK_SIDENAV_FORM.groupId).reset(null);
    }
  }

  private syncProjectGroupSelection(groups: Array<{ id: number; name: string }>): void {
    if (this.service.isCreation || this.isProcessTaskContext) return;

    const groupIdControl = this.service.form.get(this.service.TASK_SIDENAV_FORM.groupId);
    const currentGroupId = Number(groupIdControl?.value);
    const hasCurrentGroup = groups.some((group) => Number(group?.id) === currentGroupId);

    if (hasCurrentGroup) {
      this.service.pendingProjectGroupSync = false;
      return;
    }

    const defaultGroup =
      groups.find((group) => String(group?.name || '').toLowerCase() === 'general') || groups[0];

    this.service.pendingProjectGroupSync = false;

    if (!defaultGroup?.id) return;

    this.service.setGroupID(defaultGroup.id, false);
  }

  protected startTitleEdit(): void {
    if (!this.allowInlineEditing) return;
    this.editingTitle = true;
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
    this.changes.detectChanges();
    this.focusTitleInput();
  }

  protected cancelTitleEdit(): void {
    this.editingTitle = false;
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
  }

  protected saveTitleEdit(): void {
    if (!this.titleEditControl.dirty || this.titleEditControl.invalid) return;

    const value = (this.titleEditControl.value || '').trim();
    this.service.task.description = value;
    this.service.form.get(TASK_SIDENAV_FORM.description).setValue(value);
    this.editingTitle = false;
    this.titleEditControl.markAsPristine();
  }

  protected startDescriptionEdit(): void {
    if (!this.allowInlineEditing) return;
    this.editingDescription = true;
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
    this.changes.detectChanges();
    this.focusDescriptionEditor();
    requestAnimationFrame(() => this.initEditorCustomMatcher('content'));
  }

  protected cancelDescriptionEdit(): void {
    this.editingDescription = false;
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
    this.disposeEditorCustomMatcher('content');
  }

  protected saveDescriptionEdit(): void {
    if (!this.contentEditControl.dirty) return;

    const value = this.contentEditControl.value || null;
    this.service.task.content = value;
    this.service.safeContent = value ? this.sanitizer.bypassSecurityTrustHtml(value) : null;
    this.service.form.get(TASK_SIDENAV_FORM.content).setValue(value);
    this.editingDescription = false;
    this.contentEditControl.markAsPristine();
  }

  protected startInternalNoteEdit(): void {
    if (!this.allowInlineEditing) return;

    this.editingInternalNote = true;
    this.internalNoteEditControl.setValue(this.service?.task?.internalNote || null);
    this.internalNoteEditControl.markAsPristine();
    this.changes.detectChanges();
    requestAnimationFrame(() => this.focusInternalNoteEditor());
    requestAnimationFrame(() => this.initEditorCustomMatcher('internalNote'));
  }

  protected cancelInternalNoteEdit(): void {
    this.editingInternalNote = false;
    this.internalNoteEditControl.setValue(this.service?.task?.internalNote || null);
    this.internalNoteEditControl.markAsPristine();
    this.disposeEditorCustomMatcher('internalNote');
  }

  protected saveInternalNoteEdit(): void {
    if (!this.internalNoteEditControl.dirty) return;

    const value = this.internalNoteEditControl.value || null;
    this.service.task.internalNote = value;
    this.service.safeContentInternalNote = value ? this.sanitizer.bypassSecurityTrustHtml(value) : null;
    this.service.form.get(TASK_SIDENAV_FORM.internalNote).setValue(value);
    this.editingInternalNote = false;
    this.internalNoteEditControl.markAsPristine();
  }

  protected onInlineEditorMouseDown(inputType: EditorTarget, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    this.preserveInlineEditorOnBlur[inputType] = !!target?.closest('.ql-toolbar, .ql-tooltip');
  }

  protected onInlineEditorBlur(inputType: EditorTarget): void {
    if (!this.preserveInlineEditorOnBlur[inputType]) {
      if (inputType === 'internalNote') {
        this.saveInternalNoteEdit();
      }
      return;
    }

    this.preserveInlineEditorOnBlur[inputType] = false;
  }

  protected onEnter(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.saveTitleEdit();
  }

  get hasPendingInlineChanges(): boolean {
    return (
      (this.editingTitle && this.titleEditControl.dirty) ||
      (this.editingDescription && this.contentEditControl.dirty) ||
      (this.editingInternalNote && this.internalNoteEditControl.dirty) ||
      !!this.changesSummary?.hasPendingInlineChanges
    );
  }

  discardInlineChanges(): void {
    if (this.editingTitle) {
      this.cancelTitleEdit();
    }

    if (this.editingDescription) {
      this.cancelDescriptionEdit();
    }

    if (this.editingInternalNote && this.internalNoteEditControl.dirty) {
      this.cancelInternalNoteEdit();
    }

    this.changesSummary?.discardInlineChanges();
  }

  saveInlineChanges(emitEvent: boolean = true): void {
    const hasTitleChange = this.editingTitle && this.titleEditControl.dirty && !this.titleEditControl.invalid;
    const hasDescriptionChange = this.editingDescription && this.contentEditControl.dirty;
    const hasInternalNoteChange = this.editingInternalNote && this.internalNoteEditControl.dirty;
    const hasChangesSummaryChange = !!this.changesSummary?.hasPendingInlineChanges;

    if (!hasTitleChange && !hasDescriptionChange && !hasInternalNoteChange && !hasChangesSummaryChange) {
      return;
    }

    const nextDescription = hasTitleChange
      ? (this.titleEditControl.value || '').trim()
      : this.service?.task?.description || '';
    const nextContent = hasDescriptionChange
      ? this.contentEditControl.value || null
      : this.service?.task?.content || null;
    const nextInternalNote = this.internalNoteEditControl.dirty
      ? this.internalNoteEditControl.value || null
      : this.service?.task?.internalNote || null;

    this.service.task.description = nextDescription;
    this.service.task.content = nextContent;
    this.service.task.internalNote = nextInternalNote;
    this.service.safeContent = nextContent ? this.sanitizer.bypassSecurityTrustHtml(nextContent) : null;
    this.service.safeContentInternalNote = nextInternalNote
      ? this.sanitizer.bypassSecurityTrustHtml(nextInternalNote)
      : null;

    this.service.form.patchValue(
      {
        [TASK_SIDENAV_FORM.description]: nextDescription,
        [TASK_SIDENAV_FORM.content]: nextContent,
        [TASK_SIDENAV_FORM.internalNote]: nextInternalNote
      },
      { emitEvent }
    );

    this.editingTitle = false;
    this.editingDescription = false;
    this.editingInternalNote = false;
    this.titleEditControl.markAsPristine();
    this.contentEditControl.markAsPristine();
    this.internalNoteEditControl.markAsPristine();
    this.disposeEditorCustomMatcher('content');
    this.disposeEditorCustomMatcher('internalNote');

    this.changesSummary?.saveInlineChanges(emitEvent);
  }

  private syncInlineEditors(): void {
    this.titleEditControl.setValue(this.service?.task?.description || '');
    this.titleEditControl.markAsPristine();
    this.contentEditControl.setValue(this.service?.task?.content || null);
    this.contentEditControl.markAsPristine();
    this.internalNoteEditControl.setValue(this.service?.task?.internalNote || null);
    this.internalNoteEditControl.markAsPristine();
    this.editingTitle = false;
    this.editingDescription = false;
    this.editingInternalNote = false;
  }

  private focusTitleInput(): void {
    requestAnimationFrame(() => {
      const input = this.titleInput?.inputv2?.nativeElement;
      if (!input) return;

      input.focus();
      const cursorPosition = input.value?.length || 0;
      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  }

  private focusDescriptionEditor(): void {
    requestAnimationFrame(() => {
      const editor = this.elRef.nativeElement.querySelector('quill-editor .ql-editor') as HTMLElement | null;

      if (!editor) return;

      editor.focus();

      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
  }

  private focusInternalNoteEditor(): void {
    requestAnimationFrame(() => {
      const editor = this.elRef.nativeElement.querySelector('.internal-note-bound .ql-editor') as
        | HTMLElement
        | null;

      if (!editor) return;

      editor.focus();

      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
  }

  private clearImageFromQuillAtIndex(inputType: EditorTarget): void {
    const quill = this.getEditorQuill(inputType);
    if (!quill) return;

    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength() - 1;
    const [blot] = quill.getLeaf(index);

    if (blot?.statics?.blotName === 'image') {
      quill.deleteText(index - 1, 1, 'user');
    }
  }

  protected uploadImage(inputType: EditorTarget, imageDataUrl, _type, imageData): void {
    const quill = this.getEditorQuill(inputType);
    if (!quill) return;

    const selection = quill.getSelection();
    const insertIndex = selection ? selection.index : quill.getLength();

    this.clearImageFromQuillAtIndex(inputType);

    this.zone.run(() => {
      this.dialog
        .open(ImageAttachmentModalComponent, { width: Config.CONFIRM_MODAL_WIDTH })
        .afterClosed()
        .subscribe((data) => {
          const file = imageData.toFile(this.clipboardFileName || imageData.name);

          if ((data?.asAttachment && !data?.asImage) || !data) {
            this.clearImageFromQuillAtIndex(inputType);
          }

          if (data?.asAttachment) {
            this.clearImageFromQuillAtIndex(inputType);
            this.attachmentAdded.emit(file);
          }

          if (data?.asImage) {
            this.uploadFile(file, inputType, {
              resolve: (url) => {
                quill.insertEmbed(insertIndex - 1, 'image', url, 'user');
                setTimeout(() => quill.setSelection(insertIndex, 0));
              }
            });
          }

          this.clipboardFileName = '';
        });
    });
  }

  private initEditorCustomMatcher(inputType: EditorTarget): void {
    const editor = this.getEditorElement(inputType);
    if (!editor) return;

    const currentTarget =
      inputType === 'content' ? this.descriptionPasteTarget : this.internalNotePasteTarget;
    if (currentTarget === editor) return;

    editor.addEventListener('paste', this.onEditorPaste);
    if (inputType === 'content') this.descriptionPasteTarget = editor;
    else this.internalNotePasteTarget = editor;
  }

  private disposeEditorCustomMatcher(inputType: EditorTarget): void {
    const target = inputType === 'content' ? this.descriptionPasteTarget : this.internalNotePasteTarget;
    if (!target) return;

    target.removeEventListener('paste', this.onEditorPaste);
    if (inputType === 'content') this.descriptionPasteTarget = undefined;
    else this.internalNotePasteTarget = undefined;
  }

  private getEditorElement(inputType: EditorTarget): HTMLElement | null {
    const selector = inputType === 'content' ? '.quill-custom-matcher' : '.internal-note-bound';
    return this.elRef.nativeElement.querySelector(selector) as HTMLElement | null;
  }

  private getEditorQuill(inputType: EditorTarget): any {
    return inputType === 'content'
      ? this.descriptionEditor?.quillEditor || this.descriptionEditor?.__quill
      : this.internalNoteEditor?.quillEditor || this.internalNoteEditor?.__quill;
  }

  private selectQuillAttachment(inputType: EditorTarget, origin?: HTMLElement | null): void {
    const projectId = this.service.task?.project?.id || this.service.projectID || this.projectIdCtrl?.value || null;
    this.quillAttachmentSourcePicker
      .select(projectId, { photoAttachments: true, fileAttachments: true, showText: false }, origin)
      .subscribe((selection) => {
        if (!selection) return;

        if (selection.type === 'device') {
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'image/*';
          fileInput.onchange = () => {
            const file = fileInput.files?.[0];
            if (file) this.insertQuillFile(file, inputType);
          };
          fileInput.click();
          return;
        }

        const quill = this.getEditorQuill(inputType);
        if (!quill) return;

        let index = quill.getSelection()?.index ?? quill.getLength();
        selection.items.forEach((item) => {
          const url = this.authFile.transform(item.file);
          if (!url) return;
          if (item.isImage) quill.insertEmbed(index, 'image', url, 'user');
          else quill.insertText(index, item.name, { link: url }, 'user');
          index += 1;
        });
        quill.setSelection(index, 0);
      });
  }

  private insertQuillFile(file: File, inputType: EditorTarget): void {
    const quill = this.getEditorQuill(inputType);
    if (!quill) return;

    const index = quill.getSelection()?.index ?? quill.getLength();
    this.uploadFile(file, inputType, {
      resolve: (url) => {
        quill.insertEmbed(index, 'image', url, 'user');
        quill.setSelection(index + 1, 0);
      }
    });
  }

  private readonly onEditorPaste = (e: ClipboardEvent): void => {
    const clipboardData = e.clipboardData || (window as any).clipboardData;
    const isImage = clipboardData?.types?.length && Array.from(clipboardData.types).includes('Files');
    if (!isImage) return;

    const file = clipboardData.files[0];
    this.clipboardFileName = file?.name || '';
  };

  protected uploadFile(file: File, inputType: EditorTarget, opt: { resolve?: any; reject?: any } = {}) {
    const { resolve, reject } = opt;
    if (getFileType(file) === AttachmentType.FILE) {
      this.snackBarService.error(this.t.instant('ProjectCreator.imagesAllowed'));
      reject?.('Unsupported');
      return;
    }

    this.attachmentService
      .uploadAttachment(
        {
          objectType: 'wysiwyg-content',
          files: [file]
        },
        {}
      )
      .subscribe({
        next: (data) => {
          const url = `${Config.PRIVATE_ATTACHMENT}${data[0].file}`;
          if (resolve) {
            resolve(url);
          } else {
            const editor = this.getEditorQuill(inputType);
            const control = inputType === 'content' ? this.contentEditControl : this.internalNoteEditControl;
            control?.setValue(editor?.root?.innerHTML || null);
          }
        },
        error: () => {
          reject?.('Unsupported');
        }
      });
  }

  subscribeTaskUpdate() {
    const sub = this.service.taskActionEmitter.subscribe(() => this.changes.detectChanges());
    this.sub.add(sub);
  }

  openTaskEdit() {
    this.taskManager.editTask(this.service?.task, this.listTaskService.isMyWorkTaskLists);
  }

  openParentPreview(task: Task) {
    this.taskManager.previewTask(task);
  }

  navigateToProject(project?: Project) {
    if (!project) return;

    this.navigateService
      .goWithPromise('project-dashboard-summary', { projectId: project?.id })
      .then(() => this.service.closeTaskSidenav());
  }

  ngOnDestroy() {
    this.disposeEditorCustomMatcher('content');
    this.disposeEditorCustomMatcher('internalNote');
    this.sub.unsubscribe();
  }
}
